import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getSchedule } from '../../services/erpApi';
import { DEFAULT_COMPANY } from '../../constants/company';

const LABEL_W = 96;
const ROW_H   = 48;
const AXIS_H  = 40;

const TYPE_COLOR_VAR = {
  PRESS: '--cn-press', MILL: '--cn-mill', LATHE: '--cn-lathe',
  SWISS_LATHE: '--cn-lathe', LASER: '--cn-mill', PRESS_BRAKE: '--cn-press',
};

function machineTypeColor(machine) {
  return TYPE_COLOR_VAR[machine.machineType || ''] || '--cn-muted';
}

// PLANNED raised from 0.3 (near-invisible on the surface) to 0.5; opacity alone is a weak
// single channel, so PLANNED also gets a dashed border below to be readable (Cognitive Load).
const STATUS_OPACITY = {
  COMPLETED: 1.0, ACTIVE: 0.9, RELEASED: 0.55, 'NOT RELEASED': 0.5,
};

const STATUS_STYLE = {
  ACTIVE: { color: 'var(--cn-warning)' },
  RELEASED:    { color: 'var(--cn-info)'    },
  'NOT RELEASED':     { color: 'var(--cn-muted)'   },
  COMPLETED:    { color: 'var(--cn-success)' },
};

function tickIntervalFor(spanMs) {
  if (spanMs <=  2 * 3600000) return 15 * 60000;
  if (spanMs <=  8 * 3600000) return     3600000;
  if (spanMs <= 72 * 3600000) return  4 * 3600000;
  return 12 * 3600000;
}

function fmtSpan(ms) {
  if (!ms || ms <= 0) return '';
  const h = ms / 3600000;
  if (h < 1)  return `${Math.round(ms / 60000)}m`;
  if (h < 24) return `${Number.isInteger(h) ? h : h.toFixed(1)}h`;
  const d = h / 24;
  return `${Number.isInteger(d) ? d : d.toFixed(1)}d`;
}

function fmtAxisTick(ms) {
  const d    = new Date(ms);
  const day  = d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  return { day, time };
}

function fmtTs(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function fmtEttc(secs) {
  if (!secs || secs <= 0) return '—';
  if (secs < 60)   return `${secs}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  const h = Math.floor(secs / 3600);
  const m = Math.round((secs % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export default function SchedulePage({ embedded = false }) {
  const [searchParams] = useSearchParams();
  const companySlug    = searchParams.get('company') || DEFAULT_COMPANY;

  const [schedule, setSchedule]                 = useState(null);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState(null);
  const [nowMs, setNowMs]                       = useState(Date.now());
  const [selectedWoNumber, setSelectedWoNumber] = useState(null);
  const [horizonHours, setHorizonHours]         = useState(48);
  const [chartWidth, setChartWidth]             = useState(800);
  const [viewStart, setViewStart]               = useState(null);
  const [viewEnd, setViewEnd]                   = useState(null);
  const [zoomStack, setZoomStack]               = useState([]);
  const [dragOverlay, setDragOverlay]           = useState(null);

  const containerRef   = useRef(null);
  const lastDragDist   = useRef(0);
  const cursorRef      = useRef(null);

  // Measure timeline width via ResizeObserver; re-attach when schedule data renders the chart
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w) setChartWidth(Math.max(100, w - LABEL_W));
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [schedule]);

  // Reset view and zoom when switching companies
  useEffect(() => {
    setViewStart(null);
    setViewEnd(null);
    setZoomStack([]);
  }, [companySlug]);

  const load = useCallback(async () => {
    try {
      const data = await getSchedule({ company: companySlug, horizonHours });
      setSchedule(data);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companySlug, horizonHours]);

  useEffect(() => {
    setLoading(true);
    load();
    const refreshId = setInterval(load, 10000);
    const nowId     = setInterval(() => setNowMs(Date.now()), 1000);
    return () => { clearInterval(refreshId); clearInterval(nowId); };
  }, [load]);

  // Esc closes the WO drawer — overlays are universally expected to dismiss on Escape (Jakob).
  // MUST be declared before the early returns below (Rules of Hooks — a conditional hook is illegal).
  useEffect(() => {
    if (selectedWoNumber === null) return;
    const onKey = (e) => { if (e.key === 'Escape') setSelectedWoNumber(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedWoNumber]);

  if (loading) return <div style={{ padding: '32px 36px', color: 'var(--cn-muted)' }}>Loading schedule…</div>;
  if (error)   return <div style={{ padding: '32px 36px', color: 'var(--cn-danger)' }}>Error: {error}</div>;
  if (!schedule || !schedule.machines.length) {
    return <div style={{ padding: '32px 36px', color: 'var(--cn-muted)' }}>No schedule data for this company.</div>;
  }

  const { machines, wos = {} } = schedule;

  // The default window is anchored on NOW, never on the data. It used to open at the earliest
  // block in the payload, so a single op completed weeks ago parked the whole Gantt in the past
  // with the "now" line off-screen to the right — the chart read as frozen even though the
  // projection behind it was live and re-fetching every 10s.
  //
  // Floored to the hour on purpose: `nowMs` re-renders every second, and an unfloored start
  // would slide the entire chart continuously instead of holding still between hour marks.
  const horizonMs    = horizonHours * 3600000;
  const lookBackMs   = horizonMs * 0.15; // keep in-flight work visible instead of clipped at the left edge
  const defaultStart = Math.floor((nowMs - lookBackMs) / 3600000) * 3600000;

  // Active view window — falls back to the now-anchored default if no zoom applied
  const vStart  = viewStart ?? defaultStart;
  const vEnd    = viewEnd   ?? (defaultStart + horizonMs);
  const spanMs  = vEnd - vStart;
  const pxPerMs = chartWidth > 0 && spanMs > 0 ? chartWidth / spanMs : 0;

  // Adaptive tick grid
  const tickInterval = tickIntervalFor(spanMs);
  const tickOrigin   = Math.ceil(vStart / tickInterval) * tickInterval;
  const ticks = [];
  for (let t = tickOrigin; t <= vEnd; t += tickInterval) {
    ticks.push({ ms: t, px: (t - vStart) * pxPerMs });
  }
  const nowPx = (nowMs - vStart) * pxPerMs;

  // Selected WO
  const selectedWoOps = selectedWoNumber
    ? machines
        .flatMap(m => m.blocks
          .filter(b => b.woNumber === selectedWoNumber)
          .map(b => ({ ...b, displayName: m.displayName })))
        .sort((a, b) => a.opSequence - b.opSequence)
    : [];
  const selectedWoMeta = selectedWoNumber ? wos[selectedWoNumber] : null;
  const drawerOpen     = selectedWoNumber !== null;

  // Zoom actions
  function zoomIn() {
    const center = (vStart + vEnd) / 2;
    const half   = spanMs / 4;
    setZoomStack(prev => [...prev, { viewStart: vStart, viewEnd: vEnd }]);
    setViewStart(center - half);
    setViewEnd(center + half);
  }

  function zoomOut() {
    const center = (vStart + vEnd) / 2;
    setZoomStack(prev => [...prev, { viewStart: vStart, viewEnd: vEnd }]);
    setViewStart(center - spanMs);
    setViewEnd(center + spanMs);
  }

  function undoZoom() {
    if (!zoomStack.length) return;
    const prev = zoomStack[zoomStack.length - 1];
    setZoomStack(s => s.slice(0, -1));
    setViewStart(prev.viewStart);
    setViewEnd(prev.viewEnd);
  }

  function resetZoom() {
    setZoomStack([]);
    setViewStart(null);
    setViewEnd(null);
  }

  // Drag-to-zoom: window listeners capture move/up outside container bounds
  function handlePointerDown(e) {
    if (e.button !== 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x    = e.clientX - rect.left - LABEL_W;
    if (x < 0) return; // click on label column — ignore

    const startX = x;
    // Capture vStart/vEnd/pxPerMs at drag start
    const snapVStart  = vStart;
    const snapVEnd    = vEnd;
    const snapPxPerMs = pxPerMs;

    const onMove = (ev) => {
      const r  = containerRef.current?.getBoundingClientRect();
      if (!r) return;
      const cx   = ev.clientX - r.left - LABEL_W;
      const left = Math.min(startX, cx);
      const w    = Math.abs(cx - startX);
      if (w > 5) setDragOverlay({ left: Math.max(0, left), width: w });
    };

    const onUp = (ev) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup',   onUp);
      const r    = containerRef.current?.getBoundingClientRect();
      const cx   = r ? ev.clientX - r.left - LABEL_W : startX;
      const dist = Math.abs(cx - startX);
      lastDragDist.current = dist;

      if (dist > 10 && snapPxPerMs > 0) {
        const s         = Math.min(startX, cx);
        const endX      = Math.max(startX, cx);
        const newVStart = snapVStart + s    / snapPxPerMs;
        const newVEnd   = snapVStart + endX / snapPxPerMs;
        if (newVEnd - newVStart > 60000) {
          setZoomStack(prev => [...prev, { viewStart: snapVStart, viewEnd: snapVEnd }]);
          setViewStart(newVStart);
          setViewEnd(newVEnd);
        }
      }

      setDragOverlay(null);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup',   onUp);
  }

  return (
    <div style={{ position: 'relative', minHeight: '100%' }}>
      <div style={{
        padding: '32px 36px',
        marginRight: drawerOpen ? 440 : 0,
        transition: 'margin-right 0.25s cubic-bezier(0.4,0,0.2,1)',
      }}>

        {/* Page header. Inside the Plan shell the tab already names this view and the shell already
            names the company, so the title collapses to just the refresh note. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
          {!embedded && (
            <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--cn-text)', margin: 0 }}>Schedule</h1>
          )}
          <span style={{ fontSize: 12, color: 'var(--cn-dimmed)' }}>
            Auto-refresh 10s{embedded ? '' : ` · ${companySlug}`}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--cn-dimmed)' }}>
              Horizon
            </label>
            <select
              value={horizonHours}
              onChange={e => {
                const h = Number(e.target.value);
                setHorizonHours(h);
                setViewStart(null);
                setViewEnd(null);
                setZoomStack([]);
              }}
              style={{ background: 'var(--cn-surface2)', border: '1px solid rgba(168,144,96,0.2)', borderRadius: 4, color: 'var(--cn-text)', padding: '5px 10px', fontSize: 12 }}
            >
              {[24, 48, 72, 168].map(h => <option key={h} value={h}>{h}h</option>)}
            </select>
          </div>
        </div>

        {/* Legend + zoom controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
          {/* Legend */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
            {[['PRESS', '--cn-press'], ['MILL', '--cn-mill'], ['LATHE', '--cn-lathe']].map(([label, cv]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: `var(${cv})` }} />
                <span style={{ fontSize: 11, color: 'var(--cn-muted)', letterSpacing: '0.06em' }}>{label}</span>
              </div>
            ))}
            <div style={{ width: 1, background: 'rgba(168,144,96,0.2)', alignSelf: 'stretch' }} />
            {[['COMPLETED', 1.0], ['ACTIVE', 0.9], ['RELEASED', 0.55], ['NOT RELEASED', 0.3]].map(([label, opacity]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 22, height: 10, borderRadius: 2, background: 'var(--cn-gold)', opacity }} />
                <span style={{ fontSize: 11, color: 'var(--cn-muted)' }}>{label}</span>
              </div>
            ))}
            <div style={{ width: 1, background: 'rgba(168,144,96,0.2)', alignSelf: 'stretch' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 2, height: 14, background: '#f87171', borderRadius: 1 }} />
              <span style={{ fontSize: 11, color: 'var(--cn-muted)' }}>Now</span>
            </div>
          </div>

          {/* Zoom controls */}
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: 'var(--cn-dimmed)', marginRight: 4 }}>{fmtSpan(spanMs)} · drag to zoom</span>
            {[
              { label: '−', title: 'Zoom out',          action: zoomOut                          },
              { label: '+', title: 'Zoom in',            action: zoomIn                           },
              { label: '↩', title: 'Undo zoom',          action: undoZoom, disabled: !zoomStack.length },
              // Home, not "Reset" — there is now a "Reset dispatch" action in the Plan header, and
              // two differently-destructive buttons both labelled Reset in one view is a trap.
              { label: '⌂', title: 'Reset zoom to default view', action: resetZoom               },
            ].map(({ label, title, action, disabled }) => (
              <button
                key={label}
                onClick={action}
                disabled={disabled}
                title={title}
                style={{
                  background: 'var(--cn-surface2)', border: '1px solid rgba(168,144,96,0.2)',
                  borderRadius: 4, padding: '4px 9px', fontSize: 12, fontFamily: 'inherit',
                  color: disabled ? 'var(--cn-dimmed)' : 'var(--cn-text)',
                  cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1,
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Gantt chart */}
        <div
          ref={containerRef}
          onPointerDown={handlePointerDown}
          onMouseMove={(e) => {
            if (!cursorRef.current) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            if (x > LABEL_W) {
              cursorRef.current.style.display = 'block';
              cursorRef.current.style.left = `${x}px`;
            } else {
              cursorRef.current.style.display = 'none';
            }
          }}
          onMouseLeave={() => { if (cursorRef.current) cursorRef.current.style.display = 'none'; }}
          style={{
            position: 'relative', overflow: 'hidden',
            background: 'var(--cn-surface)',
            border: '1px solid rgba(168,144,96,0.1)',
            borderRadius: 8, userSelect: 'none',
            // Keep the native pointer (Jakob/Fitts) — a hidden cursor breaks aiming; a
            // full-height guideline still gives the "where am I on the timeline" cue.
          }}
        >
          {/* Vertical time-guideline at the cursor (updated via direct DOM, no re-renders) */}
          <div
            ref={cursorRef}
            style={{
              display: 'none',
              position: 'absolute',
              pointerEvents: 'none',
              zIndex: 30,
              top: 0, bottom: 0, width: 1,
              background: 'rgba(255,255,255,0.18)',
            }}
          />

          {/* Drag selection overlay */}
          {dragOverlay && (
            <div style={{
              position: 'absolute', pointerEvents: 'none', zIndex: 20,
              left: LABEL_W + dragOverlay.left, top: 0, bottom: 0, width: dragOverlay.width,
              background: 'rgba(168,144,96,0.15)',
              border: '1px solid rgba(168,144,96,0.5)',
            }} />
          )}

          {/* Time axis row */}
          <div style={{ display: 'flex', height: AXIS_H, borderBottom: '1px solid rgba(168,144,96,0.12)' }}>
            <div style={{
              width: LABEL_W, flexShrink: 0,
              background: 'var(--cn-surface2)',
              borderRight: '1px solid rgba(168,144,96,0.12)',
              cursor: 'default',
            }} />
            <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
              {ticks.map(({ ms, px }) => {
                const { day, time } = fmtAxisTick(ms);
                return (
                  <div key={ms} style={{ position: 'absolute', left: px, top: 0, bottom: 0 }}>
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 1, background: 'rgba(168,144,96,0.15)' }} />
                    <div style={{ position: 'absolute', left: 4, top: 4, fontSize: 10, color: 'var(--cn-muted)', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                      <div style={{ fontWeight: 600 }}>{time}</div>
                      <div style={{ color: 'var(--cn-dimmed)' }}>{day}</div>
                    </div>
                  </div>
                );
              })}
              {nowPx >= 0 && nowPx <= chartWidth && (
                <div style={{ position: 'absolute', left: nowPx, top: 0, bottom: 0, width: 2, background: '#f87171', zIndex: 2 }} />
              )}
            </div>
          </div>

          {/* Machine rows */}
          {machines.map((machine, rowIdx) => {
            const colorVar = machineTypeColor(machine);
            const isLast   = rowIdx === machines.length - 1;
            return (
              <div key={machine.machineId} style={{
                display: 'flex', height: ROW_H,
                borderBottom: isLast ? 'none' : '1px solid rgba(168,144,96,0.06)',
              }}>
                {/* Label */}
                <div style={{
                  width: LABEL_W, flexShrink: 0,
                  background: 'var(--cn-surface)',
                  borderRight: '1px solid rgba(168,144,96,0.12)',
                  display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px',
                  cursor: 'default',
                }}>
                  <div style={{ width: 6, height: 6, borderRadius: 1, background: `var(${colorVar})`, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--cn-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {machine.displayName || machine.machineId}
                  </span>
                </div>
                {/* Timeline */}
                <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
                  {ticks.map(({ ms, px }) => (
                    <div key={ms} style={{ position: 'absolute', left: px, top: 0, bottom: 0, width: 1, background: 'rgba(168,144,96,0.06)', zIndex: 0 }} />
                  ))}
                  {nowPx >= 0 && nowPx <= chartWidth && (
                    <div style={{ position: 'absolute', left: nowPx, top: 0, bottom: 0, width: 2, background: '#f87171', zIndex: 3 }} />
                  )}
                  {machine.blocks.map((block) => {
                    if (!block.schedStart || !block.schedEnd) return null;
                    const startMs = new Date(block.schedStart).getTime();
                    const endMs   = new Date(block.schedEnd).getTime();
                    if (endMs <= vStart || startMs >= vEnd) return null;
                    const left       = (startMs - vStart) * pxPerMs;
                    const width      = Math.max(2, (endMs - startMs) * pxPerMs);
                    const isSelected = block.woNumber === selectedWoNumber;
                    const opacity    = isSelected ? 1 : (STATUS_OPACITY[block.status] ?? 0.4);
                    return (
                      <div
                        key={`${block.woNumber}_${block.opSequence}`}
                        title={`${block.woNumber} · ${block.opName} · ${block.status} · ${block.completedQty}/${block.targetQty} pcs`}
                        onClick={() => {
                          if (lastDragDist.current > 10) { lastDragDist.current = 0; return; }
                          setSelectedWoNumber(prev => prev === block.woNumber ? null : block.woNumber);
                        }}
                        style={{
                          position: 'absolute', left, width: width - 2,
                          top: 8, height: ROW_H - 16,
                          background: `var(${colorVar})`,
                          opacity, borderRadius: 3, cursor: 'pointer',
                          // Second, non-opacity channel so PLANNED (not-yet-released) is
                          // distinguishable at a glance, not just dimmer.
                          border: block.status === 'NOT RELEASED' ? '1px dashed rgba(255,255,255,0.5)' : 'none',
                          boxSizing: 'border-box',
                          zIndex: isSelected ? 2 : 1,
                          outline: isSelected ? '2px solid white' : 'none',
                          outlineOffset: 1,
                          transition: 'opacity 0.15s',
                          display: 'flex', alignItems: 'center', paddingLeft: 5,
                          overflow: 'hidden',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = 1; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = String(isSelected ? 1 : opacity); }}
                      >
                        {width > 40 && (
                          <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', letterSpacing: '0.03em', display: 'flex', alignItems: 'center', gap: 4 }}>
                            {block.live?.isActive && (
                              <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', border: '1px solid #fff', borderTopColor: 'transparent', animation: 'schedSpin 0.8s linear infinite', flexShrink: 0 }} />
                            )}
                            {block.woNumber.replace(/^WO-[A-Z0-9]+-/, '')}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <style>{`@keyframes schedSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>

      <WoDrawer
        woNumber={selectedWoNumber}
        woMeta={selectedWoMeta}
        ops={selectedWoOps}
        open={drawerOpen}
        onClose={() => setSelectedWoNumber(null)}
      />
    </div>
  );
}

function WoDrawer({ woNumber, woMeta, ops, open, onClose }) {
  return (
    <div style={{
      position: 'fixed',
      right: 0,
      top: 0,
      height: '100vh',
      width: 440,
      background: 'var(--cn-surface)',
      borderLeft: '1px solid rgba(168,144,96,0.15)',
      boxShadow: '-12px 0 40px rgba(0,0,0,0.35)',
      transform: open ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
      zIndex: 50,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Collapse tab */}
      <button
        onClick={onClose}
        title="Collapse"
        style={{
          position: 'absolute',
          left: -30,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 30,
          height: 80,
          background: 'var(--cn-surface)',
          border: '1px solid rgba(168,144,96,0.15)',
          borderRight: 'none',
          borderRadius: '6px 0 0 6px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--cn-muted)',
          fontSize: 16,
          boxShadow: '-4px 0 12px rgba(0,0,0,0.2)',
          transition: 'color 0.15s, background 0.15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(168,144,96,0.1)';
          e.currentTarget.style.color = 'var(--cn-gold)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'var(--cn-surface)';
          e.currentTarget.style.color = 'var(--cn-muted)';
        }}
      >
        ›
      </button>

      {/* Header */}
      <div style={{
        padding: '20px 20px 16px',
        borderBottom: '1px solid rgba(168,144,96,0.1)',
        flexShrink: 0,
      }}>
        {woNumber ? (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--cn-text)', marginBottom: 4 }}>
                  {woNumber}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--cn-gold)' }}>
                    {woMeta?.partNumber}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--cn-muted)' }}>{woMeta?.partName}</span>
                </div>
              </div>
              <button
                onClick={onClose}
                style={{ background: 'none', border: 'none', color: 'var(--cn-dimmed)', cursor: 'pointer', fontSize: 18, padding: '0 4px', lineHeight: 1 }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--cn-text)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--cn-dimmed)'}
              >
                ✕
              </button>
            </div>
            {woMeta?.dueDate && (
              <div style={{ fontSize: 11, color: 'var(--cn-dimmed)', marginTop: 8 }}>
                Due: {new Date(woMeta.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--cn-muted)' }}>No work order selected</div>
        )}
      </div>

      {/* Ops list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {ops.map((op, i) => {
          const ss       = STATUS_STYLE[op.status] || STATUS_STYLE.PLANNED;
          const colorVar = TYPE_COLOR_VAR[op.machineType] || machineTypeColor({ machineType: op.machineType }) || '--cn-muted';
          const pct      = op.targetQty > 0 ? (op.completedQty / op.targetQty) * 100 : 0;

          return (
            <div
              key={`${op.machineId}_${op.opSequence}`}
              style={{
                padding: '14px 20px',
                borderBottom: i < ops.length - 1 ? '1px solid rgba(168,144,96,0.07)' : 'none',
              }}
            >
              {/* Op header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{
                  fontSize: 10, fontWeight: 800, color: 'var(--cn-dimmed)',
                  background: 'rgba(255,255,255,0.05)',
                  padding: '2px 7px', borderRadius: 3, minWidth: 32, textAlign: 'center',
                }}>
                  {op.opSequence}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--cn-text)', flex: 1 }}>{op.opName}</span>
                <span style={{
                  fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
                  padding: '2px 8px', borderRadius: 999,
                  border: `1px solid ${ss.color}`,
                  color: ss.color, background: `${ss.color}18`,
                  textTransform: 'uppercase',
                }}>
                  {op.status.replace('_', ' ')}
                </span>
              </div>

              {/* Machine row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: `var(${colorVar})`, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: `var(${colorVar})` }}>
                    {op.displayName || op.machineId || '—'}
                  </span>
                </div>
                <div style={{ flex: 1 }} />
                {op.status !== 'COMPLETED' && (
                  <div style={{ fontSize: 11, color: 'var(--cn-muted)' }}>
                    ETTC <span style={{ color: 'var(--cn-text)', fontWeight: 600 }}>{fmtEttc(op.ettcSeconds)}</span>
                  </div>
                )}
                <span style={{ fontSize: 11, color: 'var(--cn-muted)' }}>
                  {op.completedQty}/{op.targetQty}
                </span>
              </div>

              {/* Timestamps */}
              <div style={{ display: 'flex', gap: 20, marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--cn-dimmed)', marginBottom: 2 }}>Start</div>
                  <div style={{ fontSize: 11, color: 'var(--cn-muted)' }}>{fmtTs(op.schedStart)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--cn-dimmed)', marginBottom: 2 }}>End</div>
                  <div style={{ fontSize: 11, color: 'var(--cn-muted)' }}>{fmtTs(op.schedEnd)}</div>
                </div>
              </div>

              {/* Progress bar */}
              <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
                <div style={{
                  height: '100%', width: `${pct}%`,
                  background: `var(${colorVar})`,
                  borderRadius: 2, transition: 'width 0.4s',
                }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
